import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { SummaryCard } from '@nucleus-commerce/admin-kit/forms';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { ProductSummary } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import {
  useProductStoreRows,
  useStorePriceRange,
  type ProductStoreRow,
} from '../hooks/use-product-store-rows';
import { isLiveInStore } from '../schemas/live-state';

import { ProductStoreStateChip } from './product-store-state-chip';

/** Mounted only while the card is open, so a closed card asks for no prices. */
function StoreLine({
  entry,
  product,
}: {
  entry: ProductStoreRow;
  product: Pick<ProductSummary, 'handle' | 'status'>;
}) {
  const { t } = useTranslation('storeCatalog');
  const { store, row, membership } = entry;
  const range = useStorePriceRange(store.id, row?.id ?? null);

  if (membership.error) {
    return (
      <li className="border-t py-2 first:border-t-0 first:pt-0">
        <span className="mb-1 block text-[13px] font-medium">{store.name}</span>
        <ErrorState error={membership.error} onRetry={() => void membership.refetch()} compact />
      </li>
    );
  }

  const detail = row
    ? [range, row.handle === product.handle ? null : t('productStores.ownHandle')]
        .filter(Boolean)
        .join(' · ')
    : t('productStores.notInStore');

  return (
    <li className="flex items-center gap-2 border-t py-2 first:border-t-0 first:pt-0">
      <span className="flex min-w-0 flex-1 flex-col">
        {row ? (
          <Link
            to="/s/$storeId/catalog/$storeProductId"
            params={{ storeId: store.id, storeProductId: row.id }}
            className="truncate text-[13px] font-medium hover:underline"
          >
            {store.name}
          </Link>
        ) : (
          <span className="truncate text-[13px] font-medium">{store.name}</span>
        )}
        {detail ? <span className="text-muted-foreground truncate text-xs">{detail}</span> : null}
      </span>
      {membership.isPending ? (
        <Skeleton className="h-5 w-14" />
      ) : (
        <ProductStoreStateChip row={row} masterStatus={product.status} />
      )}
    </li>
  );
}

export interface ProductStoresSectionProps {
  product: Pick<ProductSummary, 'id' | 'handle' | 'status'>;
  /** Opens the publish modal; the page holds it, because Collections can open it too. */
  onPublish: () => void;
}

/**
 * Where the product is sold, in the master page's rail: one row per store the session may act on.
 * Unpublishing and scheduling happen on the store's own page, through the row's link.
 */
export function ProductStoresSection({ product, onPublish }: ProductStoresSectionProps) {
  const { t } = useTranslation('storeCatalog');
  const { rows, isPending } = useProductStoreRows(product.id);
  const live = rows.filter(({ row }) => isLiveInStore(row, product.status)).length;
  const addable = rows.some(({ row }) => row === null);

  return (
    <SummaryCard
      collapsible
      title={t('productStores.title')}
      meta={
        isPending || rows.length === 0
          ? undefined
          : t('productStores.liveCount', { count: live, total: rows.length })
      }
      tour="catalog.product-edit.stores"
    >
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-[13px]">{t('stores.noStores')}</p>
      ) : (
        <ul className="flex flex-col">
          {rows.map((entry) => (
            <StoreLine key={entry.store.id} entry={entry} product={product} />
          ))}
        </ul>
      )}
      {addable ? (
        <Can permission="products.write">
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto justify-start self-start p-0"
            onClick={onPublish}
          >
            <Plus aria-hidden />
            {t('productStores.publishAnother')}
          </Button>
        </Can>
      ) : null}
    </SummaryCard>
  );
}
